/**
 * PWA Update Prompt Component
 * Shows a toast/banner when a new version is available
 */
import { usePWA } from '@/hooks/usePWA';

export function PWAUpdatePrompt(): JSX.Element | null {
  const { needRefresh, offlineReady, updateServiceWorker, dismissUpdate } =
    usePWA();

  // Don't render anything if no updates
  if (!needRefresh && !offlineReady) {
    return null;
  }

  return (
    <div
      role="alert"
      aria-live="polite"
      className="fixed bottom-4 left-4 right-4 z-50 mx-auto max-w-md rounded-xl bg-surface p-4 shadow-lg ring-1 ring-ink-faint md:left-auto md:right-6 md:mx-0"
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-sabi-teal-light">
          {needRefresh ? (
            <svg
              className="h-5 w-5 text-sabi-teal"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          ) : (
            <svg
              className="h-5 w-5 text-success"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          )}
        </div>

        {/* Content */}
        <div className="flex-1">
          <p className="text-sm font-semibold text-ink-black">
            {needRefresh ? 'New version available!' : 'Ready to work offline'}
          </p>
          <p className="mt-0.5 text-xs text-ink-dark">
            {needRefresh
              ? 'Click update to get the latest features.'
              : 'SabiBooks can now work without internet.'}
          </p>
        </div>

        {/* Close button */}
        <button
          type="button"
          onClick={dismissUpdate}
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-ink-medium hover:bg-bg-tertiary hover:text-ink-dark"
          aria-label="Dismiss"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {/* Action button for refresh */}
      {needRefresh ? (
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={updateServiceWorker}
            className="rounded-lg bg-sabi-teal px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-sabi-teal-dark focus:outline-none focus:ring-2 focus:ring-sabi-teal focus:ring-offset-2"
          >
            Update now
          </button>
        </div>
      ) : null}
    </div>
  );
}
